<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import WahaSessionAPI from 'dashboard/api/wahaSession';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const qrCodeUrl = ref('');
const loading = ref(true);
const session = ref(null);
const showPhoneOption = ref(false);
const phoneNumber = ref('');
const pairingCode = ref('');
const requestingCode = ref(false);
let qrRefreshInterval = null;
let statusCheckInterval = null;

// A QR code that has not been scanned in two minutes is stale. Polling past
// that point only pins Rails DB connections, so stop and let the user restart.
const MAX_POLL_MS = 2 * 60 * 1000;
const pollingExpired = ref(false);
let pollingStartedAt = null;

const accountId = computed(() => route.params.accountId);
const inboxId = computed(() => route.params.inbox_id);

onMounted(async () => {
  await loadSession();
  startQRCodeRefresh();
  startStatusCheck();
});

onBeforeUnmount(() => {
  stopPolling();
});

const loadSession = async () => {
  try {
    // Find session by inbox_id
    const response = await WahaSessionAPI.getSessions(accountId.value);
    const sessions = response.data.sessions || [];
    session.value = sessions.find(s => s.inbox_id === parseInt(inboxId.value));

    if (session.value) {
      // If already authenticated, don't start QR polling
      if (session.value.status === 'WORKING') {
        console.log('Session already authenticated, skipping QR code polling');
        return;
      }
      qrCodeUrl.value = `/api/v1/accounts/${accountId.value}/waha_sessions/${session.value.id}/qr_code`;
    }
  } catch (error) {
    console.error('Failed to load session:', error);
  } finally {
    loading.value = false;
  }
};

const stopPolling = () => {
  if (qrRefreshInterval) {
    clearInterval(qrRefreshInterval);
    qrRefreshInterval = null;
  }
  if (statusCheckInterval) {
    clearInterval(statusCheckInterval);
    statusCheckInterval = null;
  }
};

const startQRCodeRefresh = () => {
  // Only start QR refresh if session is not already authenticated
  if (session.value?.status === 'WORKING') {
    console.log('Session is WORKING, skipping QR refresh');
    return;
  }

  pollingStartedAt = Date.now();
  pollingExpired.value = false;

  // Refresh QR code every 5 seconds
  qrRefreshInterval = setInterval(() => {
    if (Date.now() - pollingStartedAt > MAX_POLL_MS) {
      pollingExpired.value = true;
      stopPolling();
      return;
    }
    if (
      !showPhoneOption.value &&
      session.value &&
      session.value.status !== 'WORKING'
    ) {
      qrCodeUrl.value = `/api/v1/accounts/${accountId.value}/waha_sessions/${session.value.id}/qr_code?t=${Date.now()}`;
    }
  }, 5000);
};

const startStatusCheck = () => {
  // Check session status every 10 seconds to detect when authenticated
  statusCheckInterval = setInterval(async () => {
    if (Date.now() - pollingStartedAt > MAX_POLL_MS) {
      pollingExpired.value = true;
      stopPolling();
      return;
    }
    if (!session.value) return;

    try {
      const response = await WahaSessionAPI.getSession(
        accountId.value,
        session.value.id
      );
      const updatedSession = response.data.session;

      if (
        updatedSession.status === 'WORKING' &&
        session.value.status !== 'WORKING'
      ) {
        console.log('Session authenticated! Stopping QR code polling');
        session.value = updatedSession;
        stopPolling();
      }
    } catch (error) {
      console.error('Failed to check session status:', error);
    }
  }, 10000);
};

const restartPolling = async () => {
  pollingExpired.value = false;
  await loadSession();
  startQRCodeRefresh();
  startStatusCheck();
};


const togglePhoneOption = () => {
  showPhoneOption.value = !showPhoneOption.value;
};

const requestPairingCode = async () => {
  if (!phoneNumber.value || !session.value) return;

  requestingCode.value = true;
  try {
    const response = await WahaSessionAPI.requestPairingCode(
      accountId.value,
      session.value.id,
      phoneNumber.value
    );
    pairingCode.value = response.data.code;
  } catch (error) {
    console.error('Failed to request pairing code:', error);
    alert('Failed to request pairing code. Please try again.');
  } finally {
    requestingCode.value = false;
  }
};

const finishSetup = () => {
  router.push({
    name: 'settings_inbox_finish',
    params: {
      page: 'new',
      inbox_id: inboxId.value,
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-6 mx-0">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <span class="i-lucide-loader-2 animate-spin text-4xl text-slate-11" />
    </div>

    <div v-else>
      <!-- QR Code Section -->
      <div v-if="!showPhoneOption" class="flex flex-col items-center">
        <h2 class="mb-4 text-lg font-medium text-slate-12">
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.SCAN_QR_TITLE') }}
        </h2>
        <p class="mb-6 text-sm text-center text-slate-11">
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.SCAN_QR_DESC') }}
        </p>

        <div
          v-if="pollingExpired"
          class="flex flex-col gap-3 items-center p-4 w-64 h-64 bg-white rounded-lg border border-slate-6 justify-center"
        >
          <span class="i-lucide-refresh-cw text-3xl text-slate-11" />
          <p class="text-sm text-center text-slate-11">
            {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.QR_EXPIRED') }}
          </p>
          <button
            class="px-3 py-1.5 text-sm text-white rounded-lg bg-woot-500 hover:bg-woot-600"
            @click="restartPolling"
          >
            {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.QR_REFRESH') }}
          </button>
        </div>
        <div v-else class="p-4 bg-white rounded-lg border border-slate-6">
          <img :src="qrCodeUrl" alt="WhatsApp QR Code" class="w-64 h-64" />
        </div>

        <button
          class="mt-6 text-sm text-woot-500 hover:text-woot-600"
          @click="togglePhoneOption"
        >
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.USE_PHONE_CODE') }}
        </button>
      </div>

      <!-- Phone Code Section -->
      <div v-else class="flex flex-col max-w-md mx-auto">
        <h2 class="mb-4 text-lg font-medium text-slate-12">
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.PHONE_CODE_TITLE') }}
        </h2>
        <p class="mb-6 text-sm text-slate-11">
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.PHONE_CODE_DESC') }}
        </p>

        <div v-if="!pairingCode" class="flex flex-col gap-4">
          <input
            v-model="phoneNumber"
            type="tel"
            placeholder="+1234567890"
            class="px-4 py-2 rounded-md border border-slate-6"
          />
          <button
            :disabled="!phoneNumber || requestingCode"
            class="px-4 py-2 text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50"
            @click="requestPairingCode"
          >
            {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.REQUEST_CODE') }}
          </button>
        </div>

        <div
          v-else
          class="p-6 bg-yellow-50 rounded-lg border border-yellow-200"
        >
          <p class="mb-2 text-sm font-medium text-yellow-900">
            {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.PAIRING_CODE') }}
          </p>
          <div class="flex justify-center py-3">
            <code class="text-3xl font-bold tracking-widest text-yellow-900">
              {{ pairingCode }}
            </code>
          </div>
          <p class="mt-2 text-xs text-center text-yellow-900">
            {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.ENTER_CODE_IN_WHATSAPP') }}
          </p>
        </div>

        <button
          class="mt-6 text-sm text-woot-500 hover:text-woot-600"
          @click="togglePhoneOption"
        >
          {{ $t('INBOX_MGMT.WAHA_V2.CONFIGURE.USE_QR_CODE') }}
        </button>
      </div>

      <!-- Finish Button -->
      <div class="flex justify-end mt-8">
        <button
          class="px-6 py-2 text-white bg-woot-500 rounded-md hover:bg-woot-600"
          @click="finishSetup"
        >
          {{ $t('INBOX_MGMT.FINISH.BUTTON_TEXT') }}
        </button>
      </div>
    </div>
  </div>
</template>
